import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { ShoppingBag, Sparkles, ChevronDown, ChevronUp } from 'lucide-react'
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Badge } from '@/common/ui/badge'
import { Skeleton } from '@/common/ui/skeleton'
import { Separator } from '@/common/ui/separator'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'
import useQueryGetShopItemPresets from '../../api/hooks/useQueryGetShopItemPresets'
import useMutationApplyShopItemPreset from '../../api/hooks/useMutationApplyShopItemPreset'
import type { components } from '@/schema'

type ShopItemPreset = components['schemas']['AdminShopItemPresetModel']

const REWARD_TYPE_LABELS: Record<string, string> = {
	ITEM: 'สินค้าจริง',
	FREE_SPIN: 'ฟรีสปิน',
	CREDIT: 'เครดิต',
}

function PresetCard({ preset }: { preset: ShopItemPreset }) {
	const navigate = useNavigate()
	const [showConfirm, setShowConfirm] = useState(false)
	const [showDescription, setShowDescription] = useState(false)

	const { mutateAsync, isPending } = useMutationApplyShopItemPreset()

	const handleApply = async () => {
		try {
			const result = await mutateAsync(preset.id)
			toast.success(`สร้างสินค้า "${result.name}" สำเร็จ`)
			setShowConfirm(false)
			navigate(`/operator/shop/items/${result.id}/edit`)
		} catch {
			toast.error('ไม่สามารถสร้างสินค้าจากเทมเพลตได้')
		}
	}

	const { data } = preset

	return (
		<>
			<Card className="flex flex-col transition-shadow hover:shadow-md">
				<CardHeader className="pb-3">
					<CardTitle className="flex items-center gap-2 text-base">
						<ShoppingBag className="h-4 w-4 text-primary" />
						{preset.name}
					</CardTitle>
					<div className="flex flex-wrap gap-1.5">
						<Badge variant="outline" className="text-xs">
							{REWARD_TYPE_LABELS[data.rewardType] ?? data.rewardType}
						</Badge>
						<Badge variant="secondary" className="text-xs">
							{data.price} เพชร
						</Badge>
					</div>
					<p className="text-sm text-muted-foreground">
						ได้รับ {data.amount}{' '}
						{data.rewardType === 'CREDIT'
							? 'เครดิต'
							: data.rewardType === 'FREE_SPIN'
								? 'ฟรีสปิน'
								: 'ชิ้น'}
					</p>
				</CardHeader>

				<CardContent className="flex-1 space-y-2 pb-3">
					<button
						type="button"
						onClick={() => setShowDescription((v) => !v)}
						className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
					>
						{showDescription ? (
							<ChevronUp className="h-3 w-3" />
						) : (
							<ChevronDown className="h-3 w-3" />
						)}
						{showDescription ? 'ซ่อนรายละเอียด' : 'ดูรายละเอียด'}
					</button>
					{showDescription && (
						<p className="whitespace-pre-line text-xs leading-relaxed text-muted-foreground">
							{preset.description}
						</p>
					)}
				</CardContent>

				<CardFooter>
					<Button
						className="w-full"
						onClick={() => setShowConfirm(true)}
						disabled={isPending}
					>
						<Sparkles className="mr-2 h-4 w-4" />
						ใช้เทมเพลตนี้
					</Button>
				</CardFooter>
			</Card>

			<AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>ยืนยันสร้างสินค้าจากเทมเพลต</AlertDialogTitle>
						<AlertDialogDescription asChild>
							<div className="space-y-2">
								<p>
									จะสร้างสินค้าใหม่จากเทมเพลต &quot;{preset.name}&quot;
									สินค้าที่สร้างจะยังไม่ถูกเปิดใช้งาน
								</p>
								<p className="text-sm text-muted-foreground">
									สามารถแก้ไขราคา รูปภาพ และการตั้งค่าอื่นๆ
									ได้ในหน้าแก้ไขหลังสร้าง
								</p>
							</div>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter className="gap-2">
						<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
						<AlertDialogAction onClick={handleApply} disabled={isPending}>
							{isPending ? 'กำลังสร้าง...' : 'ยืนยัน'}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}

function PresetCardSkeleton() {
	return (
		<Card className="flex flex-col">
			<CardHeader className="pb-3">
				<Skeleton className="h-5 w-3/4" />
				<div className="flex gap-1.5">
					<Skeleton className="h-5 w-16" />
					<Skeleton className="h-5 w-20" />
				</div>
				<Skeleton className="h-4 w-1/2" />
			</CardHeader>
			<CardContent className="flex-1 pb-3">
				<Skeleton className="h-4 w-24" />
			</CardContent>
			<CardFooter>
				<Skeleton className="h-9 w-full" />
			</CardFooter>
		</Card>
	)
}

export default function ShopItemPresetGallery() {
	const { data, isLoading } = useQueryGetShopItemPresets()
	const presets = data?.presets ?? []

	return (
		<div className="space-y-4">
			<div>
				<h3 className="text-base font-semibold tracking-tight sm:text-lg">
					เทมเพลตสินค้า
				</h3>
				<p className="mt-1 text-sm text-muted-foreground">
					เลือกเทมเพลตเพื่อสร้างสินค้าสำเร็จรูป สามารถแก้ไขราคา รูปภาพ และ RTP
					preset ได้หลังจากสร้าง
				</p>
			</div>
			<Separator />

			{isLoading ? (
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
					{Array.from({ length: 3 }).map((_, i) => (
						<PresetCardSkeleton key={i} />
					))}
				</div>
			) : presets.length === 0 ? (
				<div className="flex h-32 items-center justify-center">
					<p className="text-muted-foreground">ไม่พบเทมเพลต</p>
				</div>
			) : (
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
					{presets.map((preset: ShopItemPreset) => (
						<PresetCard key={preset.id} preset={preset} />
					))}
				</div>
			)}
		</div>
	)
}
